import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import Loading from '@/components/Loading'
import {
  useRegistrationBlocksQuery,
  useRevokeRegistrationBlockMutation,
} from '@/hooks/usePlatformRegistrationBlocks'
import { usePaginationCursor } from '@/hooks/usePaginationCursor'
import { toast } from 'sonner'
import type { PlatformRegistrationBlockRow } from '@/types/platform'

function RevokeButton({ block }: { block: PlatformRegistrationBlockRow }) {
  const mut = useRevokeRegistrationBlockMutation(block.id)

  const revoke = async () => {
    try {
      await mut.mutateAsync()
      toast.success('Blocage levé')
    } catch (e) {
      toast.error('Impossible de lever le blocage')
    }
  }

  if (!block.active) return null

  return (
    <Button variant='outline' size='sm' onClick={revoke} disabled={mut.isPending}>
      {mut.isPending ? 'Déblocage…' : 'Débloquer'}
    </Button>
  )
}

export default function PlatformRegistrationBlocksPage() {
  const take = 30
  const { cursor, cursorStack, goNext: goNextCursor, goPrev } = usePaginationCursor()

  const { data, isPending, isFetching } = useRegistrationBlocksQuery({ take, cursor })

  if (isPending) return <Loading />

  const items = data?.items ?? []
  const hasMore = Boolean(data?.page?.hasMore)
  const nextCursor = data?.page?.nextCursor ?? null

  const goNext = () => goNextCursor(nextCursor)

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between gap-3'>
          <h2 className='text-xl font-semibold'>Liste noire</h2>
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='sm' onClick={goPrev} disabled={cursorStack.length === 0 || isFetching}>
              Précédent
            </Button>
            <Button variant='outline' size='sm' onClick={goNext} disabled={!hasMore || !nextCursor || isFetching}>
              Suivant
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-2'>
          {items.map((b) => (
            <div key={b.id} className='grid grid-cols-1 gap-2 rounded-lg border p-3 md:grid-cols-[1fr_auto]'>
              <div>
                <div className='font-medium'>{b.emailNorm}</div>
                <div className='text-xs text-muted-foreground'>
                  ip: {b.ip ?? '—'} • user-agent: {b.userAgent ?? '—'}
                </div>
                <div className='text-xs text-muted-foreground'>{b.reason}</div>
                <div className='text-xs text-muted-foreground'>
                  {new Date(b.createdAt).toLocaleString('fr-FR')}
                </div>
              </div>
              <div className='flex items-center gap-2 justify-end'>
                <Badge variant={b.active ? 'destructive' : 'secondary'}>
                  {b.active ? 'Actif' : 'Levé'}
                </Badge>
                <RevokeButton block={b} />
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className='text-sm text-muted-foreground'>Aucun blocage</div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
